import { fetchAuthSession } from '@aws-amplify/auth';
import { SyncOperation, SyncOperationResult } from '@chiroup/core/types/SyncOperation.type';

/**
 * Get the sync server HTTP base URL from the WebSocket URL.
 * WebSocket: wss://sync.dev.chiroup.com → HTTP: https://sync.dev.chiroup.com
 * Local: ws://localhost:1234 → HTTP: http://localhost:1235
 */
function getSyncServerHttpUrl(): string {
  const wsUrl = (import.meta as any).env?.VITE_SYNC_SERVER_URL || 'ws://localhost:1234';

  if (wsUrl.includes('localhost')) {
    // Local dev: WebSocket on 1234, HTTP on 1235
    return wsUrl.replace('ws://', 'http://').replace(':1234', ':1235');
  }

  // Deployed: same host, HTTPS
  return wsUrl.replace('wss://', 'https://').replace('ws://', 'http://');
}

async function getAuthToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || null;
  } catch {
    return null;
  }
}

/**
 * Send an operation to the sync server.
 * Returns the operation result, or throws if the request fails entirely.
 */
export async function sendSyncOperation<T = unknown>(
  operation: SyncOperation
): Promise<SyncOperationResult<T>> {
  const baseUrl = getSyncServerHttpUrl();
  const token = await getAuthToken();

  if (!token) {
    throw new Error('No auth token available');
  }

  const response = await fetch(`${baseUrl}/internal/operation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(operation),
  });

  const result = await response.json();

  if (!response.ok && !result.ok) {
    const error = new Error(result.error || `Operation failed: ${response.status}`);
    (error as any).response = { data: result, status: response.status };
    throw error;
  }

  return result as SyncOperationResult<T>;
}
